import { useState } from 'react';
import { AuthError, ModalError } from '../modal-error';
import { Box, Flex, Header } from 'components';
import { FrameEmail, FrameEmailCode } from './components';
import { ApiAuth } from 'common/api';
import { ApiError, ApiErrorCode } from '@bustickus/api';
import { ModalLoader } from 'shared/components';
import { AuthService } from 'common/services';
import { RoutingUrls } from 'common/const';

type Stage = 'email' | 'email_code';

function FrameLogin() {
  const [sessionId, setSessionId] = useState<string>('');
  const [source, setSource] = useState<string>('');
  const [codeSendedAt, setCodeSendedAt] = useState<Date>(new Date());
  const [error, setError] = useState<AuthError | null>(null);
  const [stage, setStage] = useState<Stage>('email');
  const [loading, setLoading] = useState<boolean>(false);

  const handleEmailStep = async (email: string) => {
    try {
      setLoading(true);
      const api = ApiAuth.getInstance();
      const response = await api.loginEmail(email);
      setSource(email);
      setSessionId(response.sessionId);
      setCodeSendedAt(new Date());
      setStage('email_code');
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.code === ApiErrorCode.BadRequest) {
          setError('invalid_data');
        } else if (e.code === ApiErrorCode.NotFound) {
          setError('user_not_found');
        } else {
          setError('unexpected');
        }
      } else {
        setError('unexpected');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailCodeResend = async () => {
    try {
      setLoading(true);
      const api = ApiAuth.getInstance();
      await api.resendCodeEmail(sessionId);
      setCodeSendedAt(new Date());
      setStage('email_code');
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.code === ApiErrorCode.NotFound) {
          setError('session_not_found');
        } else {
          setError('unexpected');
        }
      } else {
        setError('unexpected');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailCodeStep = async (code: string) => {
    try {
      setLoading(true);
      const api = ApiAuth.getInstance();
      const response = await api.loginEmailCode(sessionId, code);
      setCodeSendedAt(new Date());
      AuthService.getInstance().set(response.token);
      window.location.href = RoutingUrls.Main;
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.code === ApiErrorCode.BadRequest) {
          setError('invalid_data');
        } else if (e.code === ApiErrorCode.NotFound) {
          setError('code_not_found');
        } else {
          setError('unexpected');
        }
      } else {
        setError('unexpected');
      }

      setLoading(false);
    }
  };

  return (
    <div>
      {loading && <ModalLoader />}
      {error != null && (
        <ModalError
          error={error}
          onClose={() => setError(null)}
        />
      )}
      <Box padding={20}>
        <Flex
          direction='column'
          gap={20}
        >
          <Header
            align='center'
            size='bigger'
          >
            Вход
          </Header>
          {stage === 'email' && <FrameEmail onLoginClick={handleEmailStep} />}
          {stage === 'email_code' && (
            <FrameEmailCode
              email={source}
              sendedAt={codeSendedAt}
              onCodeConfirmClick={handleEmailCodeStep}
              onResendClick={handleEmailCodeResend}
              onBackClick={() => setStage('email')}
            />
          )}
        </Flex>
      </Box>
    </div>
  );
}

export default FrameLogin;
